import { useQuery } from '@tanstack/react-query';
import { Timer } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import {
  formatarDataHora,
  formatarDistancia,
  formatarMinutos,
  formatarPorcentagem,
  mensagemDeErro,
} from '@/lib/format';
import { chaves, obterSla } from '@/features/tickets/tickets.api';
import type { RelogioDeSla } from '@/features/tickets/types';

/**
 * Relógios de SLA, OLA e UC.
 *
 * Os minutos são de **tempo útil**, não de calendário: um chamado aberto na
 * sexta às 18h com prazo de 4 h vence na segunda de manhã, e mostrar "faltam
 * 62 horas" seria mentira. Por isso o consumo aparece em percentual e em
 * minutos úteis, com a data absoluta só no `title`.
 */
export function SlaPanel({ ticketId }: { ticketId: string }) {
  const sla = useQuery({
    queryKey: chaves.sla(ticketId),
    queryFn: () => obterSla(ticketId),
    // O relógio anda sozinho; sem isto a tela de um plantão inteiro mostraria
    // o consumo do momento em que foi aberta.
    refetchInterval: 60_000,
  });

  if (sla.isPending) return <p className="text-sm text-muted-foreground">Carregando prazos…</p>;

  if (sla.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {mensagemDeErro(sla.error)}
      </p>
    );
  }

  if (sla.data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
        <Timer className="h-5 w-5 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">
          Nenhuma política de prazo se aplica a este chamado.
        </p>
      </div>
    );
  }

  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {sla.data.map((relogio) => (
        <Relogio key={relogio.id} relogio={relogio} />
      ))}
    </ul>
  );
}

const ROTULO_DE_ESTADO: Record<RelogioDeSla['status'], string> = {
  RUNNING: 'Correndo',
  PAUSED: 'Pausado',
  STOPPED: 'Parado',
};

const ROTULO_DE_DESFECHO: Record<NonNullable<RelogioDeSla['outcome']>, string> = {
  MET: 'Cumprido',
  BREACHED: 'Violado',
  VOIDED: 'Anulado',
};

function Relogio({ relogio }: { relogio: RelogioDeSla }) {
  const violado = relogio.breachedAt !== null || relogio.outcome === 'BREACHED';
  const emRisco = !violado && relogio.consumedFraction >= 0.8;
  const cumprido = relogio.outcome === 'MET';

  const tom = violado
    ? 'text-destructive'
    : emRisco
      ? 'text-warning'
      : cumprido
        ? 'text-success'
        : 'text-primary';

  const preenchimento = violado
    ? 'bg-destructive'
    : emRisco
      ? 'bg-warning'
      : cumprido
        ? 'bg-success'
        : 'bg-primary';

  const percentual = Math.min(100, Math.round(relogio.consumedFraction * 100));

  return (
    <li className="space-y-3 rounded-md border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Etiqueta className="bg-accent text-foreground/80 ring-border">{relogio.kind}</Etiqueta>
            <span className="truncate text-sm font-medium">{relogio.policyName}</span>
          </div>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{relogio.metric}</p>
        </div>

        <span className={cn('whitespace-nowrap text-lg font-semibold tabular-nums', tom)}>
          {formatarPorcentagem(relogio.consumedFraction)}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuenow={percentual}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Consumo do prazo ${relogio.policyName}`}
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500', preenchimento)}
          style={{ width: `${percentual}%` }}
        />
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <Par termo="Meta" valor={formatarMinutos(relogio.targetMinutes)} />
        <Par termo="Consumido" valor={formatarMinutos(relogio.elapsedMinutes)} />
        <Par
          termo="Restante"
          valor={relogio.remainingMinutes > 0 ? formatarMinutos(relogio.remainingMinutes) : 'esgotado'}
          tom={relogio.remainingMinutes > 0 ? undefined : 'text-destructive'}
        />
        <Par
          termo="Vencimento"
          valor={formatarDistancia(relogio.dueAt)}
          titulo={formatarDataHora(relogio.dueAt)}
        />
      </dl>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2.5 text-xs">
        <span className="text-muted-foreground">{ROTULO_DE_ESTADO[relogio.status]}</span>
        {relogio.outcome ? (
          <Etiqueta
            className={cn(
              relogio.outcome === 'MET'
                ? 'bg-success/12 text-success ring-success/25'
                : relogio.outcome === 'BREACHED'
                  ? 'bg-destructive/12 text-destructive ring-destructive/25'
                  : 'bg-muted text-muted-foreground ring-border',
            )}
          >
            {ROTULO_DE_DESFECHO[relogio.outcome]}
          </Etiqueta>
        ) : null}
        {relogio.breachedAt ? (
          <span className="text-destructive" title={formatarDataHora(relogio.breachedAt)}>
            violado {formatarDistancia(relogio.breachedAt)}
          </span>
        ) : null}
      </div>
    </li>
  );
}

function Par({
  termo,
  valor,
  titulo,
  tom,
}: {
  termo: string;
  valor: string;
  titulo?: string;
  tom?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{termo}</dt>
      <dd className={cn('font-medium tabular-nums', tom)} title={titulo}>
        {valor}
      </dd>
    </div>
  );
}
